import classnames from 'classnames'
import { usePathname } from 'next/navigation'
import React, { useEffect, useRef, useState } from 'react'

import { Modal } from '@src/quince-components/src'
import NextIcon from '@src/quince-components/src/icons/nextIcon.inline.svg'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import ProductReview from '@src/quince-site-common/src/domains/ProductReview'
import plReviewPhotoPopupStateManager, {
  PLReviewPopupState,
} from '@src/quince-site-common/src/redux/globalStateManager/PLReviewPhotoPopupStateManager'
import { useAppSelector } from '@src/quince-site-common/src/redux/hooks'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'

import styles from './reviewPopupMobileNative.module.scss'
import ReviewSlide from './reviewSlide'
import { SlideReview } from './ReviewSlide.types'

// PL-only mobile photo popup. Copy of ReviewPopupMobileNative with page-based pagination (driven by
// the PL gallery via plReviewPhotoPopupStateManager). The legacy popup is left untouched.
// Prefetch the next review page once the user is within this many slides of the last loaded review.
const POPUP_PREFETCH_THRESHOLD = 2

const PLReviewPopupMobile = () => {
  const pathName = usePathname()
  const reviewReduxState = useAppSelector<PLReviewPopupState>(
    plReviewPhotoPopupStateManager,
    plReviewPhotoPopupStateManager.getReviewPopupState,
  )

  const [allReviews, setAllReviews] = useState<SlideReview[]>([])
  const [currentSlide, setCurrentSlide] = useState(0)
  // Latch so each approach to the end fires at most one fetch (see the prefetch effect below).
  const hasTriggeredNearEndRef = useRef(false)

  const onCloseModal = () => {
    plReviewPhotoPopupStateManager.hideReviewPopUp()
  }

  const handlePreviousClick = () => {
    setCurrentSlide(_currentSlide => {
      if (_currentSlide !== 0) {
        return _currentSlide - 1
      }
      return _currentSlide
    })
  }

  const handleNextClick = () => {
    setCurrentSlide(_currentSlide => {
      if (_currentSlide < allReviews.length - 1) {
        return _currentSlide + 1
      }
      return _currentSlide
    })
  }

  // Rebuild the slides whenever the review list changes — this re-runs when PL pagination appends a
  // page (productReviews grows). It deliberately does NOT touch currentSlide, so loading more never
  // yanks the user back to the start. (Non-PL sends all reviews once, so this runs only on open.)
  useEffect(() => {
    const productReviews: ProductReview[] = reviewReduxState.productReviews
    try {
      if (reviewReduxState.showPopUp) {
        const data = productReviews.map(productReview => ({
          id: productReview.id,
          imageUrls: productReview.imagesFileUrls || [],
          author: productReview.author,
          rating: productReview.rating,
          createdAt: productReview.createdAt,
          title: productReview.title,
          body: productReview.body,
          clickedImage: productReview.id === reviewReduxState.reviewId ? reviewReduxState.imageIndex : null,
        }))
        setAllReviews(data)
      } else {
        setAllReviews([])
      }
    } catch (err) {
      captureException(err, {
        module: MODULES.REVIEW,
        feature: FEATURES.REVIEW_IMAGE_POPUP,
        extraInfo: {
          originalError: JSON.stringify(err),
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewReduxState.showPopUp, reviewReduxState.productReviews.length])

  // On open (or when a different review is clicked), jump to the clicked review's slide. Separated
  // from the rebuild effect above so appends — which don't change reviewId — never reset position.
  useEffect(() => {
    if (!reviewReduxState.showPopUp) return
    const clickedIndex = reviewReduxState.productReviews.findIndex(
      productReview => productReview.id === reviewReduxState.reviewId,
    )
    if (clickedIndex >= 0) setCurrentSlide(clickedIndex)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewReduxState.showPopUp, reviewReduxState.reviewId])

  // PL pagination: when the user nears the last loaded review, ask the gallery to load the next page
  // (it appends via reviewPopupStateManager.appendReviews). Latched so each approach fires once;
  // resets when more reviews arrive (currentSlide is no longer near the grown end). No-op for non-PL
  // (hasMore is false and no fetch handler is registered).
  useEffect(() => {
    if (!reviewReduxState.showPopUp) {
      hasTriggeredNearEndRef.current = false
      return
    }
    // Guard length>0: on open the slides aren't built yet (length 0), and 0 >= 0 - threshold would
    // otherwise fire a fetch before the user has navigated anywhere.
    const isNearEnd = allReviews.length > 0 && currentSlide >= allReviews.length - POPUP_PREFETCH_THRESHOLD
    if (!isNearEnd) {
      hasTriggeredNearEndRef.current = false
      return
    }
    if (reviewReduxState.hasMore && !hasTriggeredNearEndRef.current) {
      hasTriggeredNearEndRef.current = true
      plReviewPhotoPopupStateManager.triggerFetchMore()
    }
  }, [currentSlide, allReviews.length, reviewReduxState.hasMore, reviewReduxState.showPopUp])

  useEffect(() => {
    onCloseModal()
  }, [pathName])

  return (
    <Modal
      className={styles.modal}
      open={reviewReduxState.showPopUp}
      onClose={onCloseModal}
      showCloseButton={true}
      closeButtonClassName={styles.closebutton}
    >
      <div className={styles.reviewsContainer}>
        {allReviews.map((review, index) => (
          <ReviewSlide key={review.id} productReview={review} isCurrentSlide={index === currentSlide} />
        ))}
      </div>
      <div className={styles.navWrapper}>
        <span
          className={classnames(styles.prevNav, currentSlide === 0 ? styles.disabledNav : null)}
          role="button"
          onClick={handlePreviousClick}
        >
          <NextIcon />
        </span>
        <span
          className={classnames(styles.nextNav, currentSlide === allReviews.length - 1 ? styles.disabledNav : null)}
          role="button"
          onClick={handleNextClick}
        >
          <NextIcon />
        </span>
      </div>
    </Modal>
  )
}

export default PLReviewPopupMobile
